import {
  DEFAULT_TEMPLATE_ID,
  type Site,
  TEMPLATE_DESCRIPTORS,
  type TemplateId,
  TemplateIdSchema,
} from "@hantavirus/site-config";
import type { TemplateDescriptor } from "./types.js";

let cachedTemplate: TemplateDescriptor | undefined;

export function resolveTemplateFromEnv(
  env: Record<string, string | undefined> = getRuntimeEnv(),
): TemplateDescriptor {
  const override = readTestOverride(env);
  const raw = override ?? env.VITE_SITE_TEMPLATE_ID ?? env.SITE_TEMPLATE_ID ?? DEFAULT_TEMPLATE_ID;
  const parsed = TemplateIdSchema.safeParse(raw);
  if (!parsed.success) {
    if (isCi(env)) {
      throw new Error(
        `Invalid SITE_TEMPLATE_ID '${raw}'. Allowed: ${TemplateIdSchema.options.join(", ")}`,
      );
    }
    return TEMPLATE_DESCRIPTORS[DEFAULT_TEMPLATE_ID];
  }
  return TEMPLATE_DESCRIPTORS[parsed.data];
}

export function resolveTemplateFromSite(site: Pick<Site, "templateId">): TemplateDescriptor {
  return TEMPLATE_DESCRIPTORS[site.templateId];
}

export function getActiveTemplate(): TemplateDescriptor {
  if (!cachedTemplate) {
    cachedTemplate = resolveTemplateFromEnv();
  }
  return cachedTemplate;
}

export function __resetActiveTemplateForTests(): void {
  cachedTemplate = undefined;
}

function getRuntimeEnv(): Record<string, string | undefined> {
  const viteEnv =
    typeof import.meta !== "undefined"
      ? (import.meta.env as Record<string, string | undefined>)
      : {};
  const nodeEnv =
    typeof process !== "undefined" ? (process.env as Record<string, string | undefined>) : {};
  return { ...nodeEnv, ...viteEnv };
}

function isCi(env: Record<string, string | undefined>): boolean {
  return env.CI === "true" || env.NODE_ENV === "test";
}

function readTestOverride(env: Record<string, string | undefined>): TemplateId | undefined {
  if (env.NODE_ENV === "production") return undefined;
  if (typeof window === "undefined") return undefined;
  const url = new URL(window.location.href);
  const raw = url.searchParams.get("_t");
  if (!raw) return undefined;
  const parsed = TemplateIdSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}
